import React from "react";
import { Link } from "react-router-dom";
import { formatINR } from "../lib/api";
import { resolveImageUrl } from "./ImageUpload";
import { Tag, Flame, Heart, Star, MapPin } from "lucide-react";
import { Button } from "./ui/button";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

export const PackageCard = ({ pkg, badgeLabel }) => {
  const discount = pkg.original_price && pkg.original_price > pkg.price
    ? Math.round((1 - pkg.price / pkg.original_price) * 100)
    : null;
    
  const tag = badgeLabel || (pkg.tags?.includes("trending") ? "Trending" : pkg.tags?.includes("hot") ? "Hot" : pkg.tags?.includes("budget") ? "Under Budget" : pkg.tags?.includes("festival") ? "Festival" : null);

  // 3D Tilt Logic
  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const mouseXSpring = useSpring(x);
  const mouseYSpring = useSpring(y);

  const rotateX = useTransform(mouseYSpring, [-0.5, 0.5], ["10deg", "-10deg"]);
  const rotateY = useTransform(mouseXSpring, [-0.5, 0.5], ["-10deg", "10deg"]);

  const handleMouseMove = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const width = rect.width;
    const height = rect.height;
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const xPct = mouseX / width - 0.5;
    const yPct = mouseY / height - 0.5;
    x.set(xPct);
    y.set(yPct);
  };

  const handleMouseLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.div
      style={{
        rotateX,
        rotateY,
        transformStyle: "preserve-3d",
      }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="group relative bg-white rounded-3xl overflow-hidden border border-slate-100 shadow-sm hover:shadow-2xl transition-all duration-500"
      data-testid={`package-card-${pkg.slug}`}
    >
      <Link to={`/package/${pkg.slug}`} className="block" style={{ transform: "translateZ(50px)" }}>
        <div className="relative aspect-[4/3] overflow-hidden rounded-t-3xl">
          <img
            src={resolveImageUrl(pkg.images?.[0], { width: 800, quality: 75 }) || "https://images.unsplash.com/photo-1741969494307-55394e3e4071?w=800"}
            alt={pkg.title}
            width="400"
            height="300"
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
          
          {/* Badge Overlay */}
          <div className="absolute inset-x-0 top-0 p-3 flex justify-between items-start" style={{ transform: "translateZ(70px)" }}>
            <div className="flex flex-col gap-2">
              {tag && (
                <span className="inline-flex items-center gap-1 rounded-lg bg-white/95 backdrop-blur px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-800 shadow-sm">
                  {tag === "Trending" || tag === "Hot" ? <Flame className="w-3 h-3 text-orange-500" /> : <Tag className="w-3 h-3 text-blue-500" />}
                  {tag}
                </span>
              )}
              {discount && (
                <span className="inline-flex items-center rounded-lg bg-rose-500 text-white px-2.5 py-1 text-[10px] font-bold shadow-sm uppercase tracking-wider">
                  {discount}% OFF
                </span>
              )}
            </div>
            
            <button className="w-9 h-9 rounded-full bg-white/80 backdrop-blur flex items-center justify-center text-slate-400 hover:text-rose-500 transition-colors shadow-sm">
              <Heart className="w-5 h-5" />
            </button>
          </div>

          {/* Rating Overlay */}
          <div className="absolute bottom-3 left-3 flex items-center gap-1 rounded-lg bg-black/40 backdrop-blur px-2 py-1 text-[11px] font-bold text-white">
            <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
            <span>4.8</span>
          </div>
        </div>

        <div className="p-5 space-y-3" style={{ transform: "translateZ(40px)" }}>
          <div className="space-y-1">
            <h3 className="font-serif-display text-xl leading-tight line-clamp-1 group-hover:text-primary transition-colors">{pkg.title}</h3>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground uppercase tracking-widest">
              <MapPin className="w-3 h-3" />
              <span>Available Pan India</span>
            </div>
          </div>

          <div className="flex items-end justify-between gap-4">
            <div className="flex flex-col">
              {pkg.original_price && pkg.original_price > pkg.price && (
                <span className="text-xs line-through text-slate-400">{formatINR(pkg.original_price)}</span>
              )}
              <span className="font-serif-display text-2xl font-bold text-slate-900 leading-none">{formatINR(pkg.price)}</span>
            </div>
            
            <Button className="h-11 px-5 rounded-2xl bg-[#882B7B] hover:bg-[#6d2262] text-white font-bold text-xs uppercase tracking-wider shadow-lg shadow-purple-500/20 group-hover:shadow-purple-500/40 group-hover:-translate-y-1 transition-all">
              Book Now
            </Button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};
